import {
  Box,
  Button,
  Checkbox,
  Group,
  Loader,
  Progress,
  Stack,
  Text,
  UnstyledButton,
  useMantineTheme,
  rem,
} from '@mantine/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFile, faFolder, faPaperclip } from '@fortawesome/sharp-duotone-solid-svg-icons'
import { faArrowLeft } from '@fortawesome/sharp-duotone-regular-svg-icons'
import { Attachment } from '../../../types/attachment'
import { formatFileSize } from '../../../utils/formatters'

export interface AttachmentExplorerProps {
  attachments: Attachment[]
  title?: string
  folderPath?: string[]
  onNavigate?: (id: string) => void
  onNavigateBack?: () => void
  canGoBack?: boolean
  selectable?: boolean
  selectedIds?: string[]
  onToggleSelect?: (id: string) => void
  onSelectAll?: () => void
  primarySectionTitle?: string
  secondaryAttachments?: Attachment[]
  secondarySectionTitle?: string
  primaryActionLabel?: string
  onPrimaryAction?: () => void
  primaryActionDisabled?: boolean
  progress?: { current: number; total: number }
  onCancelAction?: () => void
  actionError?: string | null
  successMessage?: string | null
  loading?: boolean
  onClose?: () => void
}

interface BreadcrumbPathProps {
  folderPath: string[]
}

function BreadcrumbPath({ folderPath }: Readonly<BreadcrumbPathProps>) {
  const theme = useMantineTheme()
  return (
    <>
      {folderPath.map((segment, i) => (
        <Group key={segment} gap="xxs" wrap="nowrap">
          {i > 0 && (
            <Text fz="xs" c="dimmed">
              {'>'}
            </Text>
          )}
          <Text
            fz="xs"
            c={i < folderPath.length - 1 ? 'dimmed' : undefined}
            fs={i < folderPath.length - 1 ? 'italic' : undefined}
            fw={i === folderPath.length - 1 ? theme.other?.fontWeightSemiBold : undefined}
          >
            {segment}
          </Text>
        </Group>
      ))}
    </>
  )
}

const COL_CHECKBOX = 24
const COL_TYPE = 30
const COL_SIZE = 50
const COL_MODIFIED = 50

interface BreadcrumbRowProps {
  folderPath: string[]
  canGoBack: boolean
  onNavigateBack?: () => void
}

function BreadcrumbRow({ folderPath, canGoBack, onNavigateBack }: Readonly<BreadcrumbRowProps>) {
  if (!folderPath.length) return null
  return (
    <Group gap="xxs" px="xs" wrap="nowrap" align="center">
      {canGoBack && (
        <Box style={{ width: COL_CHECKBOX, display: 'flex', justifyContent: 'center' }}>
          <UnstyledButton
            aria-label="Go back"
            onClick={onNavigateBack}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Box component={FontAwesomeIcon} icon={faArrowLeft} style={{ fontSize: rem(14) }} c="brand.7" />
          </UnstyledButton>
        </Box>
      )}
      <Text fz="xs" c="dimmed">
        File Path:
      </Text>
      <BreadcrumbPath folderPath={folderPath} />
    </Group>
  )
}

interface AttachmentTableProps {
  attachments: Attachment[]
  selectable?: boolean
  selectedIds?: string[]
  onToggleSelect?: (id: string) => void
  onSelectAll?: () => void
  onNavigate?: (id: string) => void
}

function AttachmentTable({
  attachments,
  selectable,
  selectedIds = [],
  onToggleSelect,
  onSelectAll,
  onNavigate,
}: Readonly<AttachmentTableProps>) {
  const theme = useMantineTheme()
  const selectableItems = attachments.filter((a) => a.selectable)
  const allSelected = selectableItems.length > 0 && selectableItems.every((a) => selectedIds.includes(a.id))
  const someSelected = !allSelected && selectableItems.some((a) => selectedIds.includes(a.id))

  return (
    // Fill the height the parent section allots; the inner list scrolls when squeezed
    <Box style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
      {/* Column headers */}
      <Group px="xs" py="xxs" bg="var(--claim-sidebar-bg)" wrap="nowrap" gap={0} align="center">
        {selectable && (
          <Box style={{ width: COL_CHECKBOX, display: 'flex', alignItems: 'center' }}>
            {onSelectAll && selectableItems.length > 0 && (
              <Checkbox
                size="xs"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={onSelectAll}
                aria-label="Select all"
              />
            )}
          </Box>
        )}
        <Text fz="xs" c="text.2" style={{ width: COL_TYPE }}>
          Type
        </Text>
        <Text fz="xs" c="text.2" style={{ flex: 1 }}>
          Name
        </Text>
        <Text fz="xs" c="text.2" style={{ width: COL_SIZE }}>
          Size
        </Text>
        <Text fz="xs" c="text.2" style={{ width: COL_MODIFIED }}>
          Last Modified
        </Text>
      </Group>

      {/* File list */}
      {attachments.length === 0 && (
        <Text fz="xs" c="dimmed" ta="center" py="md">
          No attachments found.
        </Text>
      )}
      {attachments.length > 0 && (
        <Box style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
          <Stack gap={0}>
            {attachments.map((item) => (
              <Group
                key={item.id}
                px="xs"
                py={10}
                wrap="nowrap"
                gap={0}
                style={{
                  borderBottom: `1px solid ${theme.colors.gray[2]}`,
                  cursor: item.type === 'folder' ? 'pointer' : 'default',
                }}
                onClick={item.type === 'folder' ? () => onNavigate?.(item.id) : undefined}
              >
                {selectable && (
                  <Box style={{ width: COL_CHECKBOX }}>
                    {item.selectable && (
                      <Checkbox
                        size="xs"
                        checked={selectedIds.includes(item.id)}
                        onChange={() => onToggleSelect?.(item.id)}
                        aria-label={`Select ${item.name}`}
                      />
                    )}
                  </Box>
                )}
                <Box style={{ width: COL_TYPE }}>
                  {item.type === 'folder' ? (
                    <Box component={FontAwesomeIcon} icon={faFolder} style={{ fontSize: rem('sm') }} c="brand.5" />
                  ) : (
                    <Box component={FontAwesomeIcon} icon={faFile} style={{ fontSize: rem('sm') }} c="gray.6" />
                  )}
                </Box>
                <Text fz="xs" truncate style={{ flex: 1 }}>
                  {item.name}
                </Text>
                <Text fz="xs" c="gray.6" style={{ width: COL_SIZE }}>
                  {item.type === 'folder' ? '—' : formatFileSize(item.size)}
                </Text>
                <Text fz="xs" c="gray.6" style={{ width: COL_MODIFIED }}>
                  {item.modifiedAt ?? '—'}
                </Text>
              </Group>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  )
}

export function AttachmentExplorer({
  attachments,
  title = 'Attachments',
  folderPath,
  onNavigate,
  onNavigateBack,
  canGoBack = false,
  selectable,
  selectedIds = [],
  onToggleSelect,
  onSelectAll,
  primarySectionTitle,
  secondaryAttachments,
  secondarySectionTitle,
  primaryActionLabel,
  onPrimaryAction,
  primaryActionDisabled,
  progress,
  onCancelAction,
  actionError,
  successMessage,
  loading,
  onClose,
}: Readonly<AttachmentExplorerProps>) {
  const theme = useMantineTheme()
  const isTwoSection = secondaryAttachments !== undefined

  return (
    <Stack gap="xs" h="100%" style={{ flex: 1 }}>
      {/* Header */}
      <Group gap="xs" px="xs">
        <Box style={{ width: COL_CHECKBOX, display: 'flex', justifyContent: 'center' }}>
          <UnstyledButton
            aria-label="Close attachments"
            onClick={onClose}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Box component={FontAwesomeIcon} icon={faArrowLeft} style={{ fontSize: rem(14) }} c="brand.7" />
          </UnstyledButton>
        </Box>
        <Box component={FontAwesomeIcon} icon={faPaperclip} style={{ fontSize: rem(18) }} c="brand.7" />
        <Text fz="md" c="text.2">
          {title}
        </Text>
      </Group>

      {loading && (
        <Stack align="center" py="xl">
          <Loader size="sm" />
          <Text fz="xs" c="dimmed">
            Loading attachments...
          </Text>
        </Stack>
      )}

      {!loading && isTwoSection && (
        // Grid rows are fit-content(50%) = min(content, half the area): a short section
        // sizes to its content (no extra space), a long one caps at half and scrolls,
        // and when both are long they split the height equally.
        <Box
          style={{
            display: 'grid',
            gridTemplateRows: 'fit-content(50%) fit-content(50%)',
            gap: 'var(--mantine-spacing-xs)',
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* Mail Attachments section */}
          <Box style={{ minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {primarySectionTitle && (
              <Group gap={0} px="xs">
                <Text fz="xs" fw={theme.other?.fontWeightSemiBold} c="text.2">
                  {primarySectionTitle}
                </Text>
              </Group>
            )}
            <AttachmentTable
              attachments={attachments}
              selectable={selectable}
              selectedIds={selectedIds}
              onToggleSelect={onToggleSelect}
              onSelectAll={onSelectAll}
            />
          </Box>

          {/* File Attachments (Box) section */}
          <Box style={{ minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {secondarySectionTitle && (
              <Group gap={0} px="xs">
                <Text fz="xs" fw={theme.other?.fontWeightSemiBold} c="text.2">
                  {secondarySectionTitle}
                </Text>
              </Group>
            )}
            {/* Breadcrumb for Box navigation */}
            <BreadcrumbRow folderPath={folderPath ?? []} canGoBack={canGoBack} onNavigateBack={onNavigateBack} />
            <AttachmentTable attachments={secondaryAttachments} onNavigate={onNavigate} />
          </Box>
        </Box>
      )}

      {!loading && !isTwoSection && (
        <>
          {/* Breadcrumb */}
          <BreadcrumbRow folderPath={folderPath ?? []} canGoBack={canGoBack} onNavigateBack={onNavigateBack} />

          <AttachmentTable
            attachments={attachments}
            selectable={selectable}
            selectedIds={selectedIds}
            onToggleSelect={onToggleSelect}
            onSelectAll={onSelectAll}
            onNavigate={onNavigate}
          />
        </>
      )}

      {/* Progress bar */}
      {progress && (
        <Stack gap="xxs" mt="xs">
          <Progress value={progress.total > 0 ? (progress.current / progress.total) * 100 : 0} size="sm" animated />
          <Group justify="space-between">
            <Text fz="xs" c="dimmed">
              {progress.current} of {progress.total} processed
            </Text>
            {onCancelAction && (
              <Button size="xs" variant="subtle" color="red" onClick={onCancelAction}>
                Cancel
              </Button>
            )}
          </Group>
        </Stack>
      )}

      {/* Success message */}
      {successMessage && (
        <Text fz="xs" c="green" mt="xxs">
          {successMessage}
        </Text>
      )}

      {/* Action error */}
      {actionError && (
        <Text fz="xs" c="red" mt="xxs">
          {actionError}
        </Text>
      )}

      {/* Primary action button (footer) */}
      {primaryActionLabel && onPrimaryAction && (
        <Box mt="auto" pt="xs" style={{ borderTop: `1px solid ${theme.colors.gray[2]}` }}>
          <Button
            size="xs"
            fullWidth
            disabled={(primaryActionDisabled ?? selectedIds.length === 0) || !!progress}
            loading={!!progress}
            onClick={onPrimaryAction}
          >
            {primaryActionLabel}
            {selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
          </Button>
        </Box>
      )}
    </Stack>
  )
}
